import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

const POSITIONS = ["Goalkeeper", "Center Back", "Full Back", "Defensive Mid", "Midfielder", "Winger", "Forward", "Striker"];
const FOOTS = ["Right", "Left", "Both"];

const empty = {
  name: "", jersey_number: "", position: "Midfielder", age: "",
  height_cm: "", weight_kg: "", preferred_foot: "Right", nationality: "",
  photo_url: "", notes: "",
};

const inputCls = "bg-[#0A0A0A] border-[#27272A] rounded-none focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-[#A1A1AA]";

export default function PlayerForm({ open, onOpenChange, player, bioOnly = false, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: res.data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (player) {
      setForm({ ...empty, ...Object.fromEntries(Object.entries(player).map(([k, v]) => [k, v ?? ""])) });
    } else {
      setForm(empty);
    }
  }, [player, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (bioOnly) {
      const bio = {
        age: form.age ? parseInt(form.age) : null,
        height_cm: form.height_cm ? parseFloat(form.height_cm) : null,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        preferred_foot: form.preferred_foot,
        nationality: form.nationality || null,
        photo_url: form.photo_url || null,
        notes: form.notes || null,
      };
      try {
        await api.patch(`/players/${player.id}/bio`, bio);
        toast.success("Profile updated");
        onSaved();
        onOpenChange(false);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      } finally {
        setSaving(false);
      }
      return;
    }
    const payload = {
      name: form.name,
      jersey_number: parseInt(form.jersey_number) || 0,
      position: form.position,
      age: form.age ? parseInt(form.age) : null,
      height_cm: form.height_cm ? parseFloat(form.height_cm) : null,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      preferred_foot: form.preferred_foot,
      nationality: form.nationality || null,
      photo_url: form.photo_url || null,
      notes: form.notes || null,
    };
    try {
      if (player) await api.put(`/players/${player.id}`, payload);
      else await api.post("/players", payload);
      toast.success(player ? "Player updated" : "Player added to squad");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            {bioOnly ? "Edit My Profile" : player ? "Edit Player" : "New Player"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="player-form">
          <div className="grid grid-cols-2 gap-4">
            {!bioOnly && (
            <div className="space-y-1.5 col-span-2">
              <Label className={labelCls}>Full Name</Label>
              <Input data-testid="player-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
            </div>
            )}
            {!bioOnly && (
            <div className="space-y-1.5">
              <Label className={labelCls}>Jersey #</Label>
              <Input data-testid="player-jersey-input" type="number" required value={form.jersey_number} onChange={set("jersey_number")} className={inputCls} />
            </div>
            )}
            {!bioOnly && (
            <div className="space-y-1.5">
              <Label className={labelCls}>Position</Label>
              <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
                <SelectTrigger data-testid="player-position-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A] rounded-none">
                  {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-1.5">
              <Label className={labelCls}>Age</Label>
              <Input type="number" value={form.age} onChange={set("age")} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Preferred Foot</Label>
              <Select value={form.preferred_foot} onValueChange={(v) => setForm((f) => ({ ...f, preferred_foot: v }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A] rounded-none">
                  {FOOTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Height (cm)</Label>
              <Input type="number" value={form.height_cm} onChange={set("height_cm")} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Weight (kg)</Label>
              <Input type="number" value={form.weight_kg} onChange={set("weight_kg")} className={inputCls} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className={labelCls}>Nationality</Label>
              <Input value={form.nationality} onChange={set("nationality")} className={inputCls} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className={labelCls}>Player Photo</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 shrink-0 bg-[#0A0A0A] border border-[#27272A] overflow-hidden flex items-center justify-center">
                  {form.photo_url ? (
                    <img src={resolveImg(form.photo_url)} alt="preview" className="w-full h-full object-cover object-top" />
                  ) : (
                    <Upload className="w-5 h-5 text-[#3f3f46]" />
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="player-photo-file" />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-none border-[#27272A] hover:border-white/30 hover:bg-[#1A1A1A]">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? "Uploading..." : "Upload from device"}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className={labelCls}>Or paste Photo URL</Label>
              <Input data-testid="player-photo-input" value={form.photo_url} onChange={set("photo_url")} placeholder="https://..." className={inputCls} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className={labelCls}>Notes</Label>
              <Textarea value={form.notes} onChange={set("notes")} className={inputCls} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" data-testid="player-save-button" disabled={saving} className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider">
              {saving ? "Saving..." : "Save Player"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
